import { EChartsOption } from 'echarts';
import dayjs from 'dayjs';
import { px2vw } from '@/utils';
import { ZoomParams } from '../..';

export const formatEChartData = (arr: number[]) => {
  const newArr = arr?.map(value => (value === 0 ? null : value));
  return newArr;
};

export const formatEChartDataAmount = (arr: string[]) => {
  const newArr = arr?.map(value => (value === '0.00' ? null : value));
  return newArr;
};

export const START_VALUE_ZOOM_SPAN: Record<TimeDimension, number> = {
  day: 5,
  week: 4,
  month: 3,
};

const formatMonth = date => {
  const d = dayjs(date);
  const year = d.year();
  const month = d.month() + 1;
  const currentYear = dayjs().year();

  if (year === currentYear) {
    return month + '月';
  } else {
    return year + '年' + month + '月';
  }
};

const formatWeek = date => {
  const formatDate = date.split(',');
  const date1 = dayjs(formatDate?.[0]);
  const date2 = dayjs(formatDate?.[1]);
  const year1 = date1.year();
  const year2 = date2.year();
  const currentYear = dayjs().year();

  if (year1 === currentYear && year2 === currentYear) {
    return date1?.format('MM/DD') + '-' + date2?.format('MM/DD');
  } else {
    return date1?.format('YYYY/MM/DD') + '-' + date2?.format('YYYY/MM/DD');
  }
};

export const formatDay = date => {
  const d = dayjs(date);
  const year = d.year();
  const currentYear = dayjs().year();

  if (year === currentYear) {
    return d.format('MM/DD');
  } else {
    return d?.format('YYYY/MM/DD');
  }
};

// eslint-disable-next-line react-func/max-lines-per-function
export const getOption = (data: ServiceOrderChartFormat, zoomParams?: ZoomParams): EChartsOption => {
  const timeDimension = data?.type;
  const zoomStartValue = zoomParams?.zoomStartValue ?? data?.dateArr?.[0];
  return {
    textStyle: {
      fontFamily: 'OPlusSans 3.0,sans-serif',
    },
    tooltip: {
      showContent: false,
    },
    dataZoom: [
      {
        type: 'slider',
        show: data?.dateArr?.length > START_VALUE_ZOOM_SPAN[timeDimension],
        startValue: zoomStartValue,
        endValue: data?.dateArr?.[data?.dateArr?.length - 1],
        minValueSpan: START_VALUE_ZOOM_SPAN[timeDimension] - 1,
        maxValueSpan: START_VALUE_ZOOM_SPAN[timeDimension] - 1,
        showDetail: false,
        showDataShadow: false,
        filterMode: 'none',
        zoomLock: true,
        handleIcon: 'none',
        handleSize: 0,
        fillerColor: '#C2C7D166',
        brushSelect: false,
        borderColor: 'transparent',
        height: px2vw(20),
        bottom: px2vw(12),
      },
      {
        type: 'inside',
        filterMode: 'none',
        preventDefaultMouseMove: true,
        zoomOnMouseWheel: false, //滚轮是否触发缩放
        moveOnMouseMove: true, //鼠标滚轮触发滚动
        moveOnMouseWheel: true,
        minValueSpan: START_VALUE_ZOOM_SPAN[timeDimension] - 1,
        maxValueSpan: START_VALUE_ZOOM_SPAN[timeDimension] - 1,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
    ],
    legend: {
      top: 0,
      left: 0,
      itemWidth: px2vw(16),
      itemHeight: px2vw(16),
      borderRadius: px2vw(4),
      itemGap: px2vw(50),
      padding: [px2vw(10), 0],
      textStyle: {
        fontSize: px2vw(14),
        color: '#00000073',
        lineHeight: px2vw(16),
      },
      selectedMode: false,
    },
    grid: {
      top: px2vw(70),
      left: px2vw(1),
      right: px2vw(16),
      bottom: data?.dateArr?.length > START_VALUE_ZOOM_SPAN[timeDimension] ? px2vw(38) : px2vw(16),
      containLabel: true,
    },

    xAxis: {
      type: 'category',
      data: data?.dateArr,
      axisLabel: {
        color: '#000000D9',
        fontSize: px2vw(12),
        lineHeight: px2vw(20),
        formatter: params => {
          if (timeDimension === 'month') {
            return formatMonth(params);
          } else if (timeDimension === 'week') {
            return formatWeek(params);
          } else if (timeDimension === 'day') {
            return formatDay(params);
          }
        },
      },
      inverse: true,
      axisTick: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          width: px2vw(1),
          color: '#0000000F',
        },
      },
    },
    yAxis: [
      {
        type: 'value',
        boundaryGap: [0, '15%'],
        axisLine: {
          lineStyle: {
            color: '#d9dbdf',
          },
        },
        alignTicks: true,
        axisLabel: {
          color: '#0000004D',
          fontSize: px2vw(12),
          lineHeight: px2vw(20),
        },
        splitLine: {
          lineStyle: {
            width: px2vw(1),
            color: '#0000000F',
          },
        },
      },
      {
        type: 'value',
        boundaryGap: [0, '12%'],
        axisLine: {
          lineStyle: {
            color: '#d9dbdf',
          },
        },
        alignTicks: true,
        axisLabel: {
          color: '#0000004D',
          fontSize: px2vw(12),
          lineHeight: px2vw(20),
        },
        splitLine: {
          lineStyle: {
            width: px2vw(1),
            color: '#0000000F',
          },
        },
      },
    ],
    series: [
      {
        data: formatEChartData(data?.totalOrdersArr),
        type: 'bar',
        name: '订单量（单位：单）',
        barWidth: px2vw(26),
        barMinHeight: px2vw(4),
        barGap: '80%',
        itemStyle: {
          color: '#0055FF',
          borderRadius: [px2vw(6), px2vw(6), 0, 0],
        },
        emphasis: {
          disabled: true,
        },
        label: {
          show: true,
          distance: px2vw(4),
          color: '#000000D9',
          position: 'top',
          formatter: params => {
            const length = params.value.toString()?.length;

            if (length === 4) {
              return '{a|' + params.value + '单' + '}';
            } else if (length === 3) {
              return '{b|' + params.value + '单' + '}';
            } else if (length <= 2) {
              return '{c|' + params.value + '单' + '}';
            }
          },
          rich: {
            a: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, px2vw(25), 0, 0] },
            b: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, px2vw(15), 0, 0] },
            c: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20) },
          },
        },
        yAxisIndex: 0,
      },
      {
        data: formatEChartData(data?.totalCustomersArr),
        type: 'bar',
        name: '服务人数（单位：人）',
        barWidth: px2vw(26),
        barMinHeight: px2vw(4),
        barGap: '80%',
        itemStyle: {
          color: '#AFC9FC',
          borderRadius: [px2vw(6), px2vw(6), 0, 0],
        },
        emphasis: {
          disabled: true,
        },
        label: {
          show: true,
          distance: px2vw(4),
          color: '#000000D9',
          fontSize: px2vw(14),
          fontWeight: 600,
          lineHeight: px2vw(20),
          position: 'top',
          formatter: params => params.value + '人',
        },
        yAxisIndex: 0,
      },
      {
        data: formatEChartDataAmount(data?.totalAmountArr),
        type: 'bar',
        name: '金额（单位：元）',
        barWidth: px2vw(26),
        barMinHeight: px2vw(4),
        barGap: '80%',
        itemStyle: {
          color: '#FFD37A',
          borderRadius: [px2vw(6), px2vw(6), 0, 0],
        },
        emphasis: {
          disabled: true,
        },
        label: {
          show: true,
          distance: px2vw(4),
          color: '#000000D9',
          position: 'top',
          formatter: params => {
            const numberString = params.value.toString(); // 将数字转换为字符串
            const length = parseInt(numberString.replace('.', ''))?.toString()?.length;
            if (length === 7) {
              return '{a|' + params.value + '元' + '}';
            } else if (length === 6) {
              return '{b|' + params.value + '元' + '}';
            } else if (length === 5) {
              return '{c|' + params.value + '元' + '}';
            } else if (length === 4) {
              return '{d|' + params.value + '元' + '}';
            } else if (length === 3 || length === 2 || length === 1) {
              return '{e|' + params.value + '元' + '}';
            } else {
              return '{f|' + params.value + '元' + '}';
            }
          },
          rich: {
            a: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, 0, 0, px2vw(35)] }, //7
            b: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, 0, 0, px2vw(25)] }, //6
            c: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, 0, 0, px2vw(20)] }, //5
            d: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, 0, 0, px2vw(15)] }, //4
            e: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, 0, 0, px2vw(10)] }, //3 2 1
            f: { fontSize: px2vw(14), fontWeight: 600, lineHeight: px2vw(20), padding: [0, 0, 0, px2vw(45)] }, //other
          },
        },
        yAxisIndex: 1,
      },
    ],
  };
};
